import { useState } from 'react';
import { C, MENTHE_PLEINE, TRAIT_COCHE } from '../../theme';
import { Coche } from '../../icones';

/**
 * La selection multiple du Planning (demande du porteur, 2026-08-28).
 *
 * Un mode, pas un geste cache : on l'ouvre par un bouton « Sélectionner ». L'appui long
 * aurait ete plus elegant, mais invisible pour qui ne le devine pas — et le porteur
 * venait justement de ne pas trouver un repli trop discret.
 *
 * CE QUI N'EST PAS SELECTIONNABLE, et pourquoi :
 *   - les occurrences de SERIE : les supprimer emporte la serie entiere, ce qui n'a
 *     rien a faire dans un lot ou l'on coche vite. « Retirer la série » reste un geste
 *     delibere, avec son libelle qui le dit.
 *   - les evenements venus de GOOGLE : Colette ne les possede pas.
 *   - les ROUTINES : elles se gerent dans « Ma semaine ».
 */

export type GenreSelectionnable = 'evenement' | 'tache' | 'rappel';

/** La cle d'une ligne dans la selection : son genre et son identifiant. */
export function cleDe(genre: GenreSelectionnable, id: string): string {
  return `${genre}:${id}`;
}

export interface Selection {
  actif: boolean;
  contient: (cle: string) => boolean;
  basculer: (cle: string) => void;
}

/** L'etat de la selection, tenu par le Planning. */
export function useSelection() {
  const [actif, setActif] = useState(false);
  const [choisies, setChoisies] = useState<Set<string>>(new Set());

  const quitter = () => {
    setActif(false);
    setChoisies(new Set());
  };

  return {
    actif,
    nombre: choisies.size,
    cles: [...choisies],
    ouvrir: () => setActif(true),
    quitter,
    selection: {
      actif,
      contient: (cle: string) => choisies.has(cle),
      basculer: (cle: string) =>
        setChoisies((actuelles) => {
          const suivantes = new Set(actuelles);
          if (suivantes.has(cle)) suivantes.delete(cle);
          else suivantes.add(cle);
          return suivantes;
        }),
    } satisfies Selection,
  };
}

/**
 * La case posee devant une ligne quand le mode est actif.
 *
 * Elle arrete la propagation par PRECAUTION, pas par necessite : aujourd'hui elle est
 * VOISINE du bouton de la ligne, jamais dedans, donc rien ne remonte. Le sabotage l'a
 * montre — retirer la garde ne cassait aucun test. Elle reste parce que le jour ou une
 * ligne deviendra cliquable en entier (les pilules d'Aujourd'hui le sont deja), cocher
 * declencherait aussi son geste.
 */
export function CaseDeSelection({
  cle,
  selection,
  libelle,
}: {
  cle: string;
  selection: Selection;
  libelle: string;
}) {
  if (!selection.actif) return null;
  const choisie = selection.contient(cle);

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={choisie}
      aria-label={`${choisie ? 'Retirer de la sélection' : 'Sélectionner'} : ${libelle}`}
      onClick={(e) => {
        e.stopPropagation();
        selection.basculer(cle);
      }}
      style={{
        width: 24,
        height: 24,
        flexShrink: 0,
        borderRadius: 7,
        border: `2px solid ${choisie ? MENTHE_PLEINE : C.bordureForte}`,
        background: choisie ? MENTHE_PLEINE : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        cursor: 'pointer',
        transition: 'background 0.18s ease-out, border-color 0.18s ease-out',
      }}
    >
      {choisie ? <Coche taille={14} couleur={TRAIT_COCHE} epaisseur={3} /> : null}
    </button>
  );
}
